/**
 * RaceLobbyAutoOpener — M7: auto-open/close the raceLobby window.
 *
 * Watches the room schema (mapId + gmEvent.phase). When the player is on
 * gm_race_lobby and the GM event is in the 'announced' phase, the window
 * opens automatically. It closes when the map changes or the phase advances
 * (registration/staging/live/finished/cancelled).
 *
 * Mounted once in HUD.tsx (inside WindowManagerProvider) so the window
 * appears/disappears as part of the normal game flow without requiring the
 * player to use the Quick Menu or a keybinding.
 *
 * 2026-07-09 rewrite (PTR find: no lobby UI at all for real players): the
 * old version bailed permanently when the room didn't exist at mount — on
 * PTR the HUD mounts BEFORE the Twitch-auth join completes, so the opener
 * never armed (fast local joins masked it). It also fed `getStateCallbacks`
 * the state instead of the room, so the schema subscription never bound
 * even on fast joins. Now a plain 500ms schema poll (the RaceDebug /
 * smoothness-window pattern): reads the room lazily every tick, so late
 * joins, reconnects, and already-announced events (late joiner into a live
 * lobby) all converge to the right window state within half a second.
 */

import { useEffect, useRef } from 'react';
import { useWindow } from '../../framework/WindowManagerContext';
import { gameLogger } from '../../../../utils/GameLogger';

const POLL_MS = 500;

function getRoom(): any {
  return (globalThis as any).nm?.getRoom?.() ?? null;
}

/** Maps whose 'announced' event should auto-open the lobby window — the
 *  entry lobby AND the post-race lobby (the E.2 end-race loop auto-creates
 *  the next race there; the window must follow it). */
const RACE_LOBBY_MAPS = new Set(['gm_race_lobby', 'gm_race_post_lobby']);

function isRaceLobbyVisible(): boolean {
  const room = getRoom();
  const mapId: string = room?.state?.mapId ?? '';
  const phase: string | null = room?.state?.gmEvent?.phase ?? null;
  return RACE_LOBBY_MAPS.has(mapId) && phase === 'announced';
}

export const RaceLobbyAutoOpener: React.FC = () => {
  const { openWindow, closeWindow } = useWindow('raceLobby');
  const lastVisibleRef = useRef<boolean>(false);

  useEffect(() => {
    let active = true;

    const sync = () => {
      if (!active) return;
      const visible = isRaceLobbyVisible();
      if (visible === lastVisibleRef.current) return;
      lastVisibleRef.current = visible;
      if (visible) {
        gameLogger.info('ui', 'Race lobby auto-open');
        openWindow();
      } else {
        gameLogger.info('ui', 'Race lobby auto-close');
        closeWindow();
      }
    };

    sync();
    const interval = setInterval(sync, POLL_MS);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [openWindow, closeWindow]);

  return null;
};
